import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useAuthStore } from '@/store/authStore';
import {
  type SocietyType,
  type SocietyTypeConfig,
  getSocietyTypeConfig,
} from '@/constants/societyType.constants';
import {
  type PropertyStructureConfig,
  type ResolvedPropertyTerminology,
  getPropertyStructure,
  resolvePropertyTerminology,
} from '@/constants/propertyStructure';
import { useSociety } from '@/modules/society/hooks/useSocieties';

interface PropertyTypeContextValue {
  societyType: SocietyType;
  propertyStructure: PropertyStructureConfig;
  terminology: ResolvedPropertyTerminology;
  propertyConfig: SocietyTypeConfig;
  societyName: string;
  licensedCount: number;
}

const PropertyTypeContext = createContext<PropertyTypeContextValue | undefined>(undefined);

export const PropertyTypeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const tenant = useAuthStore((s) => s.tenant);
  const user = useAuthStore((s) => s.user);
  const societyId = tenant?.id || tenant?._id || user?.tenantId;
  const { data: society } = useSociety(societyId || undefined);

  useEffect(() => {
    if (society) {
      const current = useAuthStore.getState().tenant;
      if (
        society.societyType &&
        (!current?.societyType ||
          current.societyType !== society.societyType ||
          current.name !== society.name ||
          current.licensedHouseCount !== society.licensedHouseCount)
      ) {
        useAuthStore.getState().setTenant({
          ...(current || { name: society.name || 'Society' }),
          name: society.name || current?.name || 'Society',
          societyType: society.societyType,
          licensedHouseCount: society.licensedHouseCount ?? current?.licensedHouseCount,
          registrationNumber: society.registrationNumber || current?.registrationNumber,
        });
      }
    }
  }, [society]);

  const value = useMemo<PropertyTypeContextValue>(() => {
    const rawType =
      (society as { societyType?: string } | null)?.societyType ||
      (tenant as { societyType?: string } | null)?.societyType;
    const societyType = (rawType?.toUpperCase() as SocietyType) || 'FLATS';
    const rawSettings =
      (society as { settings?: any } | null)?.settings ||
      (tenant as { settings?: any } | null)?.settings;

    const customStructure = rawSettings?.propertyStructure as Partial<PropertyStructureConfig> | undefined;
    const customTerminology = rawSettings?.propertyTerminology;

    const propertyStructure = getPropertyStructure(societyType, customStructure);
    const terminology = resolvePropertyTerminology(societyType, propertyStructure, customTerminology);
    const propertyConfig = getSocietyTypeConfig(societyType);
    const societyName = society?.name || tenant?.name || 'Society';
    const licensedCount = society?.licensedHouseCount || tenant?.licensedHouseCount || 1;

    return {
      societyType,
      propertyStructure,
      terminology,
      propertyConfig,
      societyName,
      licensedCount,
    };
  }, [society, tenant]);

  return (
    <PropertyTypeContext.Provider value={value}>
      {children}
    </PropertyTypeContext.Provider>
  );
};

export function usePropertyConfig(): SocietyTypeConfig {
  const ctx = useContext(PropertyTypeContext);
  if (!ctx) {
    const tenant = useAuthStore.getState().tenant;
    const rawType = (tenant as { societyType?: string } | null)?.societyType;
    return getSocietyTypeConfig(rawType);
  }
  return ctx.propertyConfig;
}

export function usePropertyStructure(): PropertyStructureConfig {
  const ctx = useContext(PropertyTypeContext);
  if (!ctx) {
    const tenant = useAuthStore.getState().tenant;
    const rawType = (tenant as { societyType?: string } | null)?.societyType;
    const rawSettings = (tenant as { settings?: any } | null)?.settings;
    return getPropertyStructure(rawType, rawSettings?.propertyStructure);
  }
  return ctx.propertyStructure;
}

export function useSocietyType(): SocietyType {
  const ctx = useContext(PropertyTypeContext);
  if (!ctx) {
    const tenant = useAuthStore.getState().tenant;
    const rawType = (tenant as { societyType?: string } | null)?.societyType;
    return (rawType?.toUpperCase() as SocietyType) || 'FLATS';
  }
  return ctx.societyType;
}

export function usePropertyTerminology(): ResolvedPropertyTerminology {
  const ctx = useContext(PropertyTypeContext);
  if (!ctx) {
    const tenant = useAuthStore.getState().tenant;
    const rawType = (tenant as { societyType?: string } | null)?.societyType;
    const rawSettings = (tenant as { settings?: any } | null)?.settings;
    const structure = getPropertyStructure(rawType, rawSettings?.propertyStructure);
    return resolvePropertyTerminology(rawType, structure, rawSettings?.propertyTerminology);
  }
  return ctx.terminology;
}

export interface PropertyLabelProps {
  type?: 'singular' | 'plural' | 'propertyNumber' | 'address';
  prefix?: string;
  suffix?: string;
  fallback?: string;
  transform?: 'uppercase' | 'lowercase' | 'capitalize' | 'none';
}

export const PropertyLabel: React.FC<PropertyLabelProps> = ({
  type = 'singular',
  prefix = '',
  suffix = '',
  transform = 'none',
}) => {
  const config = usePropertyConfig();

  let text = config.singularLabel;
  if (type === 'plural') text = config.pluralLabel;
  if (type === 'propertyNumber') text = config.propertyNumberLabel;
  if (type === 'address') text = config.addressLabel;

  if (transform === 'uppercase') text = text.toUpperCase();
  if (transform === 'lowercase') text = text.toLowerCase();

  return <>{prefix}{text}{suffix}</>;
};
